import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SearchInput } from "../components";
import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
import styles from "./history.module.scss";
import { TransactionListItem, ActiveFilterChips } from "./components";
import { TransactionFilters, DEFAULT_FILTERS, consumePendingFilters, setPendingFilters } from "./filter-page/history.filters";
import { getRefundStatusForDisplay, getTransactionStatusForDisplay, getTransactionTitleAndCategory, TransactionItem } from "../../../model/transaction";
import { useDebounce } from "../../../hooks/useDebounce";
import { useLazyGetCustomerTransactionsQuery } from "../../../service/transaction-service";
import { formatDate, formatTime } from "./history.utils";
import { FilterIcon } from "../../../assets/customer-settings-icons";
import { formatCurrency } from "../../../utils";
import Can from "../../../guards/Can";
import RefundTransactionModal from "../../../common/components/refund-transaction-modal/RefundTransactionModal";
import { CustomerPermissions } from "../../../model/permissions/permissions";

const PAGE_SIZE = 10;
const LOAD_THRESHOLD_PX = 100;

// Module-level cache: survives SPA navigation (component unmount/remount)
// but resets on full page refresh since the JS module is re-evaluated.
let cachedFilters: TransactionFilters | null = null;

export const CustomerTransactionHistoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [activeFilters, setActiveFilters] = useState<TransactionFilters>(() => consumePendingFilters() ?? cachedFilters ?? DEFAULT_FILTERS);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [refundReference, setRefundReference] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const debouncedSearchTerm = useDebounce(searchValue, 500);

  const hasActiveFilters = useMemo(
    () =>
      activeFilters.category !== DEFAULT_FILTERS.category ||
      activeFilters.status !== DEFAULT_FILTERS.status ||
      activeFilters.period !== DEFAULT_FILTERS.period ||
      activeFilters.startDate !== DEFAULT_FILTERS.startDate ||
      activeFilters.endDate !== DEFAULT_FILTERS.endDate,
    [activeFilters]
  );

  const [getTransactions, { isFetching, error }] = useLazyGetCustomerTransactionsQuery();

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  const isLoadingPageRef = useRef(false);
  const currentPageRef = useRef(1);

  const loadMore = useCallback(() => {
    if (isLoadingPageRef.current || !hasMoreRef.current) return;
    const nextPage = currentPageRef.current + 1;
    currentPageRef.current = nextPage;
    fetchTransactionsRef.current(nextPage, false);
  }, []);

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const checkIfAtBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasMoreRef.current || isLoadingPageRef.current) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < LOAD_THRESHOLD_PX) {
      loadMoreRef.current();
    }
  }, []);

  const checkIfAtBottomRef = useRef(checkIfAtBottom);
  checkIfAtBottomRef.current = checkIfAtBottom;

  const setupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!sentinelRef.current || !scrollContainerRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMoreRef.current && !isLoadingPageRef.current) {
          loadMoreRef.current();
        }
      },
      { root: scrollContainerRef.current, threshold: 0 }
    );
    observerRef.current.observe(sentinelRef.current);
  }, []);

  const setupObserverRef = useRef(setupObserver);
  setupObserverRef.current = setupObserver;

  const fetchTransactions = useCallback((page: number, reset: boolean = false) => {
    const dateFrom = activeFilters.startDate ? activeFilters.startDate.toISOString().split("T")[0] : undefined;
    const dateTo = activeFilters.endDate
      ? (() => {
          const d = activeFilters.endDate!;
          return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
        })()
      : undefined;

    isLoadingPageRef.current = true;

    getTransactions({
      page,
      pageSize: PAGE_SIZE,
      searchQuery: debouncedSearchTerm || undefined,
      category: activeFilters.category ?? undefined,
      status: activeFilters.status ?? undefined,
      dateFrom,
      dateTo,
    }, false)
      .unwrap()
      .then((response) => {
        const newTransactions = response.items || [];
        if (reset) {
          setTransactions(newTransactions);
        } else {
          setTransactions((prev) => [...prev, ...newTransactions]);
        }
        setHasMore(response.hasNextPage);
        isLoadingPageRef.current = false;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            checkIfAtBottomRef.current();
            setupObserverRef.current();
          });
        });
      })
      .catch((err) => {
        console.error("Error fetching transactions:", err);
        setHasMore(false);
        isLoadingPageRef.current = false;
      });
  }, [debouncedSearchTerm, activeFilters, getTransactions]);

  const fetchTransactionsRef = useRef(fetchTransactions);
  fetchTransactionsRef.current = fetchTransactions;

  const setScrollContainer = useCallback((node: HTMLDivElement | null) => {
    scrollContainerRef.current = node;
    setupObserverRef.current();
  }, []);

  const setSentinel = useCallback((node: HTMLDivElement | null) => {
    sentinelRef.current = node;
    setupObserverRef.current();
  }, []);

  useEffect(() => {
    cachedFilters = activeFilters;
  }, [activeFilters]);

  useEffect(() => {
    setTransactions([]);
    currentPageRef.current = 1;
    setHasMore(true);
    fetchTransactionsRef.current(1, true);
  }, [debouncedSearchTerm, activeFilters]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleClear = () => {
    setSearchValue("");
  };

  const handleTransactionClick = (reference: string, category: number) => {
    if (!reference) return;
    // The refund screen looks the refund up by the original transaction's reference
    if (transactions.find(tx => tx.reference === reference)?.refundStatus != null) {
      setRefundReference(reference);
      return;
    }
    navigate(`/history/${reference}?category=${category}`, { state: { category } });
  };

  return (
    <CustomerPageLayout showBackButton={true} className={styles.historyLayout} title={t("history.web.transactionHistory")} onBack={() => navigate(-1)}>
      <div className={styles.pageContainer}>
        <div className={styles.historyHeader}>
          <div className={styles.searchContainer}>
            <SearchInput
              value={searchValue}
              onChange={handleSearchChange}
              onClear={handleClear}
              placeholder={t("history.web.searchPlaceholder")}
              name="transactionSearch"
              className={styles.search}
            />
            <button className={styles.filterBtn} onClick={() => { setPendingFilters(activeFilters); navigate('filter'); }}>
              <FilterIcon className={styles.filterIcon} />
              {hasActiveFilters && <span className={styles.filterBadge} />}
            </button>
          </div>
          <ActiveFilterChips filters={activeFilters} onChange={setActiveFilters} />
        </div>

        <Can permission={CustomerPermissions.ViewTransactionHistory}>
        <div className={styles.transactionListWrapper}>
          <div className={styles.transactionList} ref={setScrollContainer}>
            {transactions.length > 0 ? (
              <>
                {transactions.map((transaction) => (
                  <TransactionListItem
                    key={transaction.reference}
                    id={transaction.reference}
                    metaData={getTransactionTitleAndCategory(transaction, t)}
                    transactionId={transaction.reference}
                    amount={formatCurrency(transaction.amount.amount, { currency: transaction.amount?.currency })}
                    date={`${formatDate(transaction.createdAt)}, ${formatTime(transaction.createdAt).toLocaleLowerCase()}`}
                    status={getRefundStatusForDisplay(transaction.refundStatus) ?? getTransactionStatusForDisplay(transaction.status)}
                    onClick={handleTransactionClick}
                  />
                ))}
                {isFetching && (
                  <div className={styles.loadingMore}>
                    <div className={styles.spinner} />
                    <span>{t("history.web.loadingMore")}</span>
                  </div>
                )}
                {hasMore && <div ref={setSentinel} style={{ height: '50px', minHeight: '50px' }} />}
                {error && (
                  <div className={styles.errorItem}>
                    <p>{t("history.web.failedToLoadTransactions")}</p>
                    <button className={styles.retryButton} onClick={() => fetchTransactionsRef.current(currentPageRef.current, false)}>
                      {t("energyManagement.retry")}
                    </button>
                  </div>
                )}
                {!hasMore && !error && (
                  <div className={styles.endOfList}>
                    <p>{t("history.web.reachedTheEnd")}</p>
                  </div>
                )}
              </>
            ) : error ? (
              <div className={styles.errorEmpty}>
                <p>{t("history.web.failedToLoadTransactions")}</p>
                <button className={styles.retryButton} onClick={() => fetchTransactionsRef.current(1, true)}>
                  {t("energyManagement.retry")}
                </button>
              </div>
            ) : isFetching ? (
              <div className={styles.loadingMore}>
                <div className={styles.spinner} />
                <span>{t("history.web.loadingTransactions")}</span>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>{t("history.web.noTransactionsFound")}</p>
                {(searchValue || hasActiveFilters) && (
                  <>
                    <p className={styles.emptyStateHint}>
                      {searchValue && hasActiveFilters
                        ? t("history.web.tryDifferentSearchOrFilters")
                        : searchValue
                        ? t("history.web.tryDifferentSearch")
                        : t("history.web.tryAdjustingFilters")}
                    </p>
                    <div className={styles.clearActions}>
                      {searchValue && hasActiveFilters ? (
                        <button className={styles.clearSearchButton} onClick={() => { handleClear(); setActiveFilters(DEFAULT_FILTERS); }}>
                          {t("history.clearAll")}
                        </button>
                      ) : searchValue ? (
                        <button className={styles.clearSearchButton} onClick={handleClear}>
                          {t("history.web.clearSearch")}
                        </button>
                      ) : (
                        <button className={styles.clearSearchButton} onClick={() => setActiveFilters(DEFAULT_FILTERS)}>
                          {t("history.web.clearFilters")}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        </Can>
      </div>
      {refundReference && <RefundTransactionModal reference={refundReference} onClose={() => setRefundReference(null)} />}
    </CustomerPageLayout>
  );
};
